import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';
import { useCallback, useEffect, useRef, useState } from 'react';
import { showToast } from '@/utils/toast';

/** 手动刷新/同步的最小间隔（毫秒） */
export const TEMU_REFRESH_MIN_INTERVAL_MS = 3000;

function defaultOnBlocked(reason, seconds) {
  if (reason === 'busy') {
    showToast(fixedUi("正在处理中，请稍候"), 'warning');
    return;
  }
  if (reason === 'cooldown') {
    showToast(fixedUiTpl('操作过于频繁，请 {0} 秒后再试', seconds), 'warning');
  }
}

/**
 * 防止刷新/同步按钮被频繁点击导致重复请求。
 * - run：用于页面初次加载（不受冷却限制，但仍防并发）
 * - refresh：用于按钮点击（冷却 + 并发保护）
 * - run/refresh 的入参会原样传给 action（如商品指定同步的 payload）
 */
export function useGuardedRefresh(action, options = {}) {
  const {
    minIntervalMs = TEMU_REFRESH_MIN_INTERVAL_MS,
    onBlocked = defaultOnBlocked,
  } = options;

  const actionRef = useRef(action);
  actionRef.current = action;

  const runningRef = useRef(false);
  const lastManualAtRef = useRef(0);
  const [refreshing, setRefreshing] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  const updateCooldown = useCallback(() => {
    const leftMs = minIntervalMs - (Date.now() - lastManualAtRef.current);
    setCooldownSeconds(leftMs > 0 ? Math.ceil(leftMs / 1000) : 0);
  }, [minIntervalMs]);

  useEffect(() => {
    if (cooldownSeconds <= 0) return undefined;
    const timer = setInterval(updateCooldown, 250);
    return () => clearInterval(timer);
  }, [cooldownSeconds, updateCooldown]);

  const invoke = useCallback(async (guarded, args = []) => {
    const now = Date.now();

    if (runningRef.current) {
      if (guarded) onBlocked?.('busy');
      return false;
    }

    if (guarded) {
      const waitMs = minIntervalMs - (now - lastManualAtRef.current);
      if (lastManualAtRef.current > 0 && waitMs > 0) {
        const seconds = Math.ceil(waitMs / 1000);
        onBlocked?.('cooldown', seconds);
        setCooldownSeconds(seconds);
        return false;
      }
      lastManualAtRef.current = now;
      setCooldownSeconds(0);
    }

    runningRef.current = true;
    setRefreshing(true);
    try {
      await actionRef.current(...args);
      return true;
    } finally {
      runningRef.current = false;
      setRefreshing(false);
      if (guarded) {
        updateCooldown();
      }
    }
  }, [minIntervalMs, onBlocked, updateCooldown]);

  const run = useCallback((...args) => invoke(false, args), [invoke]);
  const refresh = useCallback((...args) => invoke(true, args), [invoke]);

  return {
    run,
    refresh,
    refreshing,
    cooldownSeconds,
    refreshDisabled: refreshing || cooldownSeconds > 0,
  };
}
